import { useState, useCallback, useRef, useEffect } from 'react';
import { HOUR_HEIGHT_PX } from '../types';

const MIN_DURATION_MINUTES = 15;

interface ResizeState {
  eventId: string;
  originalHeight: number;
  currentHeight: number;
  startY: number;
}

interface UseEventResizeResult {
  resizingEventId: string | null;
  resizeHeight: number | null;
  onResizeStart: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
}

export function useEventResize(
  onResize: (eventId: string, newDurationMinutes: number) => void,
  hourHeightPx: number = HOUR_HEIGHT_PX,
  snapMinutes: number = 15,
): UseEventResizeResult {
  const pxPerMinute = hourHeightPx / 60;
  const [state, setState] = useState<ResizeState | null>(null);
  const stateRef = useRef<ResizeState | null>(null);
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const onResizeStart = useCallback(
    (eventId: string, originalHeight: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const initial: ResizeState = {
        eventId,
        originalHeight,
        currentHeight: originalHeight,
        startY: e.clientY,
      };
      stateRef.current = initial;
      setState(initial);
    },
    [],
  );

  useEffect(() => {
    if (!state) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!stateRef.current) return;
      const delta = e.clientY - stateRef.current.startY;
      const rawHeight = stateRef.current.originalHeight + delta;
      const minHeight = MIN_DURATION_MINUTES * pxPerMinute;
      const snappedHeight =
        Math.round(Math.max(rawHeight, minHeight) / (snapMinutes * pxPerMinute)) *
        (snapMinutes * pxPerMinute);
      stateRef.current = { ...stateRef.current, currentHeight: snappedHeight };
      setState({ ...stateRef.current });
    };

    const handleMouseUp = () => {
      if (!stateRef.current) return;
      const { eventId, currentHeight } = stateRef.current;
      const durationMinutes = Math.round(currentHeight / pxPerMinute);
      onResizeRef.current(eventId, durationMinutes);
      stateRef.current = null;
      setState(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // Only re-register listeners when starting/stopping resize (not on every pixel move).
    // The stateRef handles intermediate updates without triggering re-subscriptions.
  }, [state !== null]);

  return {
    resizingEventId: state?.eventId ?? null,
    resizeHeight: state?.currentHeight ?? null,
    onResizeStart,
  };
}
